/**
 * Cuánto tarda un mensaje en aparecer, y por cuál de los caminos llegó.
 *
 * ── POR QUÉ EXISTE ────────────────────────────────────────────────────────────────────────────
 * El founder lo reportó así: «mensajes que no llegan en tiempo real, demoras, retrasos». Al ir a
 * medirlo el 04-sep-2026 apareció el problema de fondo: sólo se podía medir el tramo de AFUERA.
 *
 *     WhatsApp → nuestra base     3,6 s de mediana · 6,8 s el p95 · 9 s el peor
 *
 * Ese número junta WhatsApp, Baileys, Evolution y el webhook en uno solo, y del tramo de adentro
 * —base → pantalla— no había ni un dato. Así no se puede decidir nada: se puede reescribir el canal
 * entero y descubrir después que el tiempo se iba en Evolution.
 *
 * ── LO QUE MIDE, Y POR QUÉ LA RUTA ES EL CAMINO ───────────────────────────────────────────────
 * `admin_rum_events.route` guarda PATRONES, no direcciones. Acá el patrón útil es por cuál de los
 * tres caminos entró el mensaje — que es la pregunta de fondo, porque hoy hay tres y ninguno es
 * responsable. Es el mismo criterio que ya usa `cronometrarAviso`, donde la ruta es el tipo de
 * aviso.
 *
 * 🚨 `reconciliacion` NO ES UN CAMINO, ES UNA FALLA. Ese es el sondeo de 60 s que rescata lo que el
 * canal se perdió. Que aparezca en el tablero como una ruta más es a propósito: es la única forma
 * de contar cuántas veces el tiempo real deja pasar un mensaje. Si esa fila crece, el canal está
 * roto aunque nadie se queje.
 *
 * ── LO QUE NO HACE ────────────────────────────────────────────────────────────────────────────
 * 🪤 NO ARREGLA NADA Y NO DEBE INTENTARLO. Es el paso F0: primero se ve, después se cura. Un módulo
 * de medición que además reintenta o repara vuelve imposible saber si la mejora fue del arreglo o
 * de la reparación — que es exactamente cómo un reconciliador nocturno tapó durante semanas el
 * conteo doble de los contadores.
 */
import { encolarRum } from '@/lib/analytics/panelRum';
import { devLog } from '@/lib/utils';

/** Por dónde entró el mensaje a la pantalla. */
export type CaminoDelMensaje = 'broadcast' | 'replicacion' | 'reconciliacion';

/** Qué le pasó al canal. Los tres últimos hoy no los detecta nadie. */
export type EstadoDelCanal = 'conectado' | 'error' | 'timeout' | 'cerrado';

export interface IdentidadRum {
  tenant_id: string | null | undefined;
  user_id: string | null | undefined;
}

/**
 * Tope de `admin_rum_events.value_ms`, replicado del CHECK de la tabla.
 *
 * 🪤 SE RECORTA EN LUGAR DE DESCARTAR, Y SÓLO ACÁ. Un mensaje rescatado por el sondeo puede tener
 * minutos de antigüedad; si se dejara pasar tal cual, `encolarRum` lo tiraría por pasarse del tope
 * y perderíamos justo el evento que más importa contar. Lo que interesa de ese camino es CUÁNTAS
 * VECES ocurre, no la edad exacta del mensaje. Los otros dos caminos nunca se acercan al tope —si
 * alguno lo hiciera, el recorte es la señal de que algo está muy mal, no un detalle.
 */
const TOPE_MS = 120_000;

/**
 * Registra que un mensaje llegó a la pantalla, y cuánto tardó desde que WhatsApp lo fechó.
 *
 * 🪤 EL ORIGEN ES `created_at`, QUE ES EL RELOJ DE WHATSAPP, no el nuestro. El webhook lo escribe
 * como `new Date(msg.timestamp * 1000)`. Medir desde nuestra propia escritura daría un número
 * bonito y falso: escondería justamente el tramo de afuera, que hoy son 3,6 segundos.
 *
 * 🪤 Y EL RELOJ DEL CELULAR DEL CLIENTE PUEDE ESTAR ADELANTADO. Eso da latencias negativas, que
 * `encolarRum` descarta. Se descartan también acá, explícitamente, para que no parezca un olvido.
 */
export function medirLlegadaDeMensaje(
  camino: CaminoDelMensaje,
  fechaDelMensaje: string | null | undefined,
  identidad: IdentidadRum,
): void {
  try {
    if (!fechaDelMensaje || !identidad.tenant_id || !identidad.user_id) return;
    const origen = new Date(fechaDelMensaje).getTime();
    if (!Number.isFinite(origen)) return;

    const latencia = Date.now() - origen;
    if (latencia < 0) return; // reloj del emisor adelantado: no es una medición

    encolarRum({
      tenant_id: identidad.tenant_id,
      user_id: identidad.user_id,
      route: `/chat/${camino}`,
      metric: 'chat_msg',
      value_ms: Math.min(latencia, TOPE_MS - 1),
      cache_hit: null,
    });
  } catch {
    /* la telemetría no rompe el chat */
  }
}

/**
 * Registra un cambio de estado del canal y cuánto vivió hasta ese momento.
 *
 * 🚨 ES LA MEDICIÓN QUE HOY NO EXISTE EN NINGÚN LADO. Los 22 lugares del módulo que abren un canal
 * llaman a `.subscribe()` sin callback, así que un canal muerto se ve exactamente igual que uno
 * sano: la pantalla sigue diciendo que está conectada y no llega nada hasta refrescar. Es la causa
 * más probable de «no me llegó y al refrescar apareció».
 *
 * 🪤 `cerrado` NO ES UNA FALLA, Y HAY QUE LEERLO ASÍ EN EL TABLERO. Lo emite nuestro propio
 * `removeChannel` al desmontar —cambiar de pantalla, cerrar la pestaña— así que va a ser el estado
 * más frecuente y eso es normal. Los que importan son `error` y `timeout`: ésos son canales que se
 * murieron solos. Confundirlos es exactamente cómo el intento anterior terminó recreando el canal
 * en un lazo (ver `elCanalEnVivoTieneUnSoloDueno`).
 *
 * @param desdeMs marca de `Date.now()` de cuando se creó el canal. Para `conectado` mide cuánto
 *   tardó en suscribirse; para los otros tres, cuánto duró vivo antes de cerrarse o romperse.
 */
export function medirEstadoDelCanal(
  estado: EstadoDelCanal,
  desdeMs: number,
  identidad: IdentidadRum,
): void {
  try {
    if (!identidad.tenant_id || !identidad.user_id) return;
    const vivio = Date.now() - desdeMs;
    if (!Number.isFinite(vivio) || vivio < 0) return;

    if (estado !== 'conectado') {
      // En desarrollo esto no se manda (ver `MIDE` en panelRum), así que el log es lo único que
      // hace visible una caída mientras se trabaja en local.
      devLog.warn(`[chat] el canal en vivo cambió a "${estado}" tras ${Math.round(vivio / 1000)}s`);
    }

    encolarRum({
      tenant_id: identidad.tenant_id,
      user_id: identidad.user_id,
      route: `/canal/${estado}`,
      metric: 'chat_canal',
      value_ms: Math.min(vivio, TOPE_MS - 1),
      cache_hit: null,
    });
  } catch {
    /* la telemetría no rompe el chat */
  }
}

/**
 * Traduce el estado que reporta supabase-js al vocabulario de acá.
 *
 * 🪤 `SUBSCRIBED` ES EL ÚNICO BUENO. Los otros tres significan que el canal no está entregando
 * nada, y hoy ninguno se mira. Un estado desconocido se trata como error a propósito: equivocarse
 * hacia «algo anda mal» hace ruido; equivocarse hacia «todo bien» esconde el problema.
 */
export function estadoDesdeSupabase(status: string): EstadoDelCanal {
  switch (status) {
    case 'SUBSCRIBED': return 'conectado';
    case 'TIMED_OUT': return 'timeout';
    case 'CLOSED': return 'cerrado';
    default: return 'error';
  }
}
